import {
  BoxGeometry,
  Color,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
} from 'three'
import { makeBoxGeometryFromCorner } from '../../helpers/three'
import { getColorFromTheme } from '../../helpers/theme'
import BasePrinterDriver from './base-printer-driver'
import { ThreeItemKeys, ThreeItemTypes } from '../../constants/three'
import { materialColors } from '../../constants/theme'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'

class BaseMiniPrinterDriver extends BasePrinterDriver {
  static readonly defaultLayerHeightMm = 10
  static readonly gcodeExtension = 'mpp'
  static readonly hasCartesianGrid = true
  static readonly hasFullPrintableZone = true
  static readonly hasGhostPlacement = true
  static readonly hasHalfPrintableZone = false
  static readonly hasPolarGrid = false
  static readonly name: string = 'MiniPrinter'
  static readonly printerOptions = {
    head: {
      type: 'select' as const,
      options: ['1k', '2k'],
      default: '1k',
    },
  }

  static readonly type: string = 'miniprinter'

  protected static readonly stlModel: string
  protected static readonly stlModelXOffset: number
  protected static readonly stlModelYOffset: number
  protected static readonly stlModelZOffset: number

  static get optionConstraints() {
    return {
      head: {
        '2k': [
          // Loss of 155mm in X- (2K nozzle bulk)
          {
            offsetX_MM: 0,
            offsetY_MM: 0,
            offsetZ_MM: 0,
            width_MM: 155,
            depth_MM: this.DEPTH_MM,
            height_MM: this.HEIGHT_MM,
          },
        ],
      },
    }
  }

  static readonly defaultGrid = 'cartesian' as const

  protected static readonly HEIGHT_MM: number = 0
  protected static readonly WIDTH_MM: number = 0
  protected static readonly DEPTH_MM: number = 0

  private static readonly CARTESIAN_ZONE_LINE_WIDTH_MM = 5
  private static readonly CARTESIAN_ZONE_RANGE_MM = 100

  protected static get HEIGHT_M() { return this.HEIGHT_MM / 1000 }
  protected static get WIDTH_M() { return this.WIDTH_MM / 1000 }
  protected static get DEPTH_M() { return this.DEPTH_MM / 1000 }
  private static get CARTESIAN_ZONE_RANGE_M() { return this.CARTESIAN_ZONE_RANGE_MM / 1000 }
  private static get CARTESIAN_ZONE_LINE_WIDTH_M() { return this.CARTESIAN_ZONE_LINE_WIDTH_MM / 1000 }

  static async getCartesianZone(gridColor: string): Promise<Object3D> {
    const depthM = this.DEPTH_M
    const widthM = this.WIDTH_M

    const cubeCartesianGroup = new Group()
    cubeCartesianGroup.name = 'Cartesian Zone'
    cubeCartesianGroup.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.CARTESIAN_ZONE

    const boxMaterial = new MeshBasicMaterial({ color: 'white', side: DoubleSide })
    const borderMaterial = new MeshBasicMaterial({
      color: new Color(getColorFromTheme(`--color-picker-${gridColor}-solid`)),
      side: DoubleSide,
    })

    // Z offset to avoid z-fighting: sink borders by half their depth plus a small epsilon
    const zFightingOffset = -(this.CARTESIAN_ZONE_LINE_WIDTH_M / 2) - 0.001

    const minYBorderGeometry = new BoxGeometry(widthM, this.CARTESIAN_ZONE_LINE_WIDTH_M, this.CARTESIAN_ZONE_LINE_WIDTH_M)
    const minYBorderMesh = new Mesh(minYBorderGeometry, borderMaterial)
    minYBorderMesh.position.set(widthM / 2, 0, zFightingOffset)
    cubeCartesianGroup.add(minYBorderMesh)

    const maxYBorderGeometry = new BoxGeometry(widthM, this.CARTESIAN_ZONE_LINE_WIDTH_M, this.CARTESIAN_ZONE_LINE_WIDTH_M)
    const maxYBorderMesh = new Mesh(maxYBorderGeometry, borderMaterial)
    maxYBorderMesh.position.set(widthM / 2, depthM, zFightingOffset)
    cubeCartesianGroup.add(maxYBorderMesh)

    const minXBorderGeometry = new BoxGeometry(this.CARTESIAN_ZONE_LINE_WIDTH_M, depthM, this.CARTESIAN_ZONE_LINE_WIDTH_M)
    const minXBorderMesh = new Mesh(minXBorderGeometry, borderMaterial)
    minXBorderMesh.position.set(0, depthM / 2, zFightingOffset)
    cubeCartesianGroup.add(minXBorderMesh)

    const maxXBorderGeometry = new BoxGeometry(this.CARTESIAN_ZONE_LINE_WIDTH_M, depthM, this.CARTESIAN_ZONE_LINE_WIDTH_M)
    const maxXBorderMesh = new Mesh(maxXBorderGeometry, borderMaterial)
    maxXBorderMesh.position.set(widthM, depthM / 2, zFightingOffset)
    cubeCartesianGroup.add(maxXBorderMesh)

    for (let offset = this.CARTESIAN_ZONE_RANGE_M; offset < depthM; offset += this.CARTESIAN_ZONE_RANGE_M) {
      const geometry = new BoxGeometry(widthM, this.CARTESIAN_ZONE_LINE_WIDTH_M, this.CARTESIAN_ZONE_LINE_WIDTH_M)
      const mesh = new Mesh(geometry, boxMaterial)
      mesh.position.set(widthM / 2, offset, zFightingOffset)
      cubeCartesianGroup.add(mesh)
    }

    for (let offset = this.CARTESIAN_ZONE_RANGE_M; offset < widthM; offset += this.CARTESIAN_ZONE_RANGE_M) {
      const geometry = new BoxGeometry(this.CARTESIAN_ZONE_LINE_WIDTH_M, depthM, this.CARTESIAN_ZONE_LINE_WIDTH_M)
      const mesh = new Mesh(geometry, boxMaterial)
      mesh.position.set(offset, depthM / 2, zFightingOffset)
      cubeCartesianGroup.add(mesh)
    }

    return cubeCartesianGroup
  };

  static async getPrintableZone(type: PrintableZoneType, color: string): Promise<Object3D> {
    if (type === 'half') {
      throw new Error('Half printable zone not supported for MiniPrinter')
    }

    const geometry = makeBoxGeometryFromCorner(this.WIDTH_M, this.DEPTH_M, this.HEIGHT_M)

    const material = new MeshStandardMaterial({
      metalness: 0.4,
      roughness: 0.8,
      transparent: true,
      opacity: 0.4,
      color: new Color(getColorFromTheme(`--color-picker-${color}-solid`)),
      side: DoubleSide,
    })

    const finalZone = new Mesh(geometry, material)

    return finalZone
  };

  static getDefaultUserData() {
    return {}
  }

  static async getMesh(colorOffset: number, forcedColor?: string) {
    return new Promise<Object3D>((resolve, reject) => {
      const loader = new STLLoader()
      loader.load(this.stlModel, (geometry) => {
        const printerMesh = new Mesh(geometry, this.getPrinterMaterial(colorOffset, forcedColor))
        printerMesh.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.PRINTER_MESH
        printerMesh.scale.set(0.0009, 0.0009, 0.0009)
        printerMesh.position.set(this.stlModelXOffset, this.stlModelYOffset, this.stlModelZOffset)
        resolve(printerMesh)
      }, () => { }, (error) => {
        reject(error)
      })
    })
  }

  protected static getPrinterMaterial(offset: number, forcedColor?: string) {
    return new MeshStandardMaterial({
      color: getColorFromTheme(`--color-picker-${forcedColor ?? materialColors[offset % materialColors.length]}-solid`),
      side: DoubleSide,
      metalness: 0.3,
      roughness: 0.3,
      wireframe: this.stlModel ? false : true,
    })
  }
}

export default BaseMiniPrinterDriver
